import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { OutputData } from "@editorjs/editorjs";

import TopicsDetail from "./TopicsDetail";
import { ImageSourceSetData } from "../../../../common/classes/ImageSourceSet";
import { SonicTermData } from "../../../../common/classes/SonicTerm";

import {
  genTopicUpdateArgs,
  valTopicImages,
  valTopicRequiredItem,
} from "../../../mypage/user_settings/topics/EditTopicsFunc";

import { useAppSelector } from "../../../../redux/hooks";
import { getAuthToken } from "../../../../redux/reducers/auth";

import { getTermOptions } from "../../../../utils/GeneralUtils";
import { RequestState } from "../../../../utils/RequestState";
import { FieldState } from "../../../../utils/ValidationFunctions";
import { TermType, useGetTerm } from "../../../../utils/useTerm";
import {
  useGetTopicsAdmin,
  useUpdateTopicAdmin,
} from "../../../../utils/useTopics";
import { useGetFacility } from "../../../../utils/useFacility";

const TopicsDetailContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const auth_token = useAppSelector(getAuthToken);

  const queryArgs = { auth_token: auth_token, id: id };

  const {
    topicsData: { items: topic, authors: author },
    isLoading: topicLoading,
  } = useGetTopicsAdmin(queryArgs);
  const { mutateAsync, mutateLoading } = useUpdateTopicAdmin([
    "topicsAdmin",
    queryArgs,
  ]);
  const topicTypes = useGetTerm(TermType.TopicTypes, undefined, -1).termData
    .entries;

  const {
    items: { facilities },
  } = useGetFacility({ per: -1 });

  const genFacilityOptions = () => {
    const options: [string, string][] = [["", "施設を選択"]];
    facilities.map((f) => {
      options.push([f.id, f.name]);
    });

    return options;
  };

  const [fieldState, setFieldState] = useState<FieldState>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<{ required: boolean; imgs: boolean }>({
    required: false,
    imgs: false,
  });
  const [showStatusMenu, setShowStatusMenu] = useState<boolean>(false);
  const [mainImage, setMainImage] = useState<File | ImageSourceSetData>();
  const [article, setArticle] = useState<OutputData>();
  const [requestState, setRequestState] = useState<RequestState>(
    RequestState.Waiting
  );

  useEffect(() => {
    if (topicLoading || topic?.[0]?.id !== id) return;

    setMainImage(topic[0].imgs?.[0]);
    setArticle(topic[0].content);

    if (topic[0]?.game) setGames(topic[0].game);

    setIsLoading(false);
  }, [topicLoading]);

  // MyGame
  const [games, setGames] = useState<Array<SonicTermData>>();

  const handleGames = (value: Array<SonicTermData>) => {
    setGames([...value]);
  };

  const handleStatusMenu = () => {
    setShowStatusMenu(!showStatusMenu);
  };

  const handleInputChange = (
    value: File | string | undefined,
    name: string
  ) => {
    const f = fieldState;
    if (value === undefined) {
      if (name !== "sonic_imgs") f[name] = ["", false];
    } else {
      setError({ required: false, imgs: false });
      if (value instanceof File) setMainImage(value);
      else f[name] = [value, true];
      setRequestState(RequestState.Pending);
    }
    setFieldState(f);
  };

  // set editorjs data
  const handleSaveArticle = (outputData: OutputData) => {
    setError({ required: false, imgs: false });
    setArticle(outputData.blocks.length ? outputData : undefined);
  };

  // save
  const handleSave = async () => {
    if (!auth_token || !id) return;

    const valRequired: boolean = valTopicRequiredItem(
      "edit",
      fieldState,
      article,
      mainImage
    );
    const valImages: boolean = valTopicImages(mainImage, article);

    if (valRequired === false || valImages === false) {
      setError({ required: !valRequired, imgs: !valImages });
      return;
    }

    setError({ required: false, imgs: false });

    const updateArgs = genTopicUpdateArgs(
      fieldState,
      article,
      mainImage,
      games
    );

    if (!updateArgs) return;

    setRequestState(RequestState.Doing);
    await mutateAsync({ auth_token: auth_token, id: id, update: updateArgs });

    setIsLoading(true);
    setRequestState(RequestState.Success);
  };

  return (
    <TopicsDetail
      topic={topic[0]}
      author={author[0]}
      mainImage={mainImage}
      article={article}
      deleteMainImage={() => setMainImage(undefined)}
      isLoading={isLoading || topicLoading || mutateLoading}
      selectStatus={
        typeof fieldState?.["sonic_status"]?.[0] === "string"
          ? fieldState["sonic_status"][0]
          : ""
      }
      setSelectStatus={handleInputChange}
      handleUpdate={handleSave}
      handleInputChange={handleInputChange}
      type_options={getTermOptions(topicTypes)}
      games={games}
      handleGames={handleGames}
      facilityOptions={genFacilityOptions()}
      showStatusMenu={showStatusMenu}
      handleStatusMenu={handleStatusMenu}
      handleSaveArticle={handleSaveArticle}
      error={error}
      requestState={requestState}
      setRequestState={setRequestState}
    />
  );
};

export default TopicsDetailContainer;
